import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { ROUTES_METADATA, DEFAULT_META, BASE_DOMAIN } from '../../config/routesMetadata.config.js'

export const useMetadataStore = defineStore('metadata', () => {
  const route = useRoute()

  const currentMeta = ref({
    title: DEFAULT_META.title,
    description: DEFAULT_META.description,
    url: DEFAULT_META.url,
  })

  const metaTags = computed(() => ({
    title: currentMeta.value.title,
    meta: [
      { name: 'description', content: currentMeta.value.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: currentMeta.value.url },
      { property: 'og:title', content: currentMeta.value.title },
      { property: 'og:description', content: currentMeta.value.description },
      { property: 'og:image', content: DEFAULT_META.image },
      { property: 'og:image:width', content: DEFAULT_META.imageWidth },
      { property: 'og:image:height', content: DEFAULT_META.imageHeight },
      { property: 'og:site_name', content: DEFAULT_META.title },
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: currentMeta.value.url },
      { property: 'twitter:title', content: currentMeta.value.title },
      {
        property: 'twitter:description',
        content: currentMeta.value.description,
      },
      { property: 'twitter:image', content: DEFAULT_META.image },
    ],
  }))

  watch(
    () => route.fullPath,
    () => {
      generateMetadata()
    },
    { immediate: true }
  )

  function setCurrentMeta({ title, description }) {
    currentMeta.value = {
      title,
      description,
      url: `${BASE_DOMAIN}${route.fullPath}`,
    }
  }

  function generateMetadata() {
    const config = ROUTES_METADATA[route.name]
    if (config?.getDynamicMeta) return
    if (!config) {
      setCurrentMeta({
        title: DEFAULT_META.title,
        description: DEFAULT_META.description,
      })
      return
    }
    setCurrentMeta({
      title: config.meta.title,
      description: config.meta.description,
    })
  }

  function setDynamicData(data) {
    const config = ROUTES_METADATA[route.name]
    if (!config?.getDynamicMeta) return
    const dynamic = config.getDynamicMeta(route, data)
    if (!dynamic?.title) return
    setCurrentMeta({
      title: dynamic.title,
      description: dynamic.description || config.meta.description,
    })
  }

  return {
    metaTags,
    setDynamicData,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMetadataStore, import.meta.hot))
}
